import { UseFormReturn } from 'react-hook-form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form'
import { Button } from '@/components/ui/button'
import { Plus, X } from 'lucide-react'
import { type ForensicsEnvironmentFormData } from './schema'
import { useVaults } from '@/features/vaults/hooks'
import { useComputeObjects } from './hooks'
import { objectsApi } from '@/features/objects/api'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import type { ObjectVersion } from '@/types/entities'

type BackendResponse<T> = T | { success: boolean; data: T }

interface ForensicsFormProps {
    form: UseFormReturn<ForensicsEnvironmentFormData>
    isEditing?: boolean
}

export function ForensicsForm({ form, isEditing }: ForensicsFormProps) {
    const { data: vaultsResponse } = useVaults()
    const vaults = (vaultsResponse as any)?.data || vaultsResponse || []
    const { data: computeObjects } = useComputeObjects()
    const computeObjectsList = Array.isArray(computeObjects) ? computeObjects : []

    const servers = form.watch('servers') || []

    const addServer = () => {
        const current = form.getValues('servers') || []
        form.setValue('servers', [...current, { computeObjectId: '', computeObjectVersionId: undefined }])
    }

    const removeServer = (index: number) => {
        const current = form.getValues('servers') || []
        form.setValue('servers', current.filter((_, i) => i !== index))
    }

    return (
        <div className="space-y-4">
            <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                    <FormItem>
                        <FormLabel>Environment Name</FormLabel>
                        <FormControl>
                            <Input placeholder="Enter environment name" {...field} />
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name="vaultId"
                render={({ field }) => (
                    <FormItem>
                        <FormLabel>Vault</FormLabel>
                        <Select
                            onValueChange={(val) => field.onChange(parseInt(val, 10))}
                            value={field.value ? String(field.value) : undefined}
                            disabled={isEditing}
                        >
                            <FormControl>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select vault" />
                                </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                                {(Array.isArray(vaults) ? vaults : []).map((v: any) => (
                                    <SelectItem key={v.id} value={String(v.id)}>
                                        {v.vaultname || v.name || v.VaultID || `Vault ${v.id}`}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <FormMessage />
                    </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name="securityGroupId"
                render={({ field }) => (
                    <FormItem>
                        <FormLabel>Security Group ID</FormLabel>
                        <FormControl>
                            <Input placeholder="sg-xxxxxxxxx" {...field} />
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name="awsAccountId"
                render={({ field }) => (
                    <FormItem>
                        <FormLabel>AWS Account ID</FormLabel>
                        <FormControl>
                            <Input placeholder="123456789012" {...field} />
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                    <FormItem>
                        <FormLabel>Description</FormLabel>
                        <FormControl>
                            <Textarea placeholder="Optional description" {...field} />
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                )}
            />

            {/* Servers section */}
            <div className="space-y-3">
                <div className="flex items-center justify-between">
                    <FormLabel>Servers</FormLabel>
                    <Button type="button" variant="outline" size="sm" onClick={addServer}>
                        <Plus className="h-4 w-4 mr-1" />
                        Add Server
                    </Button>
                </div>
                {servers.map((_server, index) => (
                    <ServerRow
                        key={index}
                        index={index}
                        form={form}
                        computeObjects={computeObjectsList}
                        onRemove={() => removeServer(index)}
                    />
                ))}
                {servers.length === 0 && (
                    <p className="text-sm text-muted-foreground">No servers added. Click "Add Server" to add compute objects to this environment.</p>
                )}
            </div>
        </div>
    )
}

function ServerRow({
    index,
    form,
    computeObjects,
    onRemove,
}: {
    index: number
    form: UseFormReturn<ForensicsEnvironmentFormData>
    computeObjects: Array<{ ObjectID: string; ObjectDescription: string }>
    onRemove: () => void
}) {
    const selectedObjectId = form.watch(`servers.${index}.computeObjectId`)
    const [versionsForObject, setVersionsForObject] = useState<ObjectVersion[]>([])

    // Fetch versions when an object is selected
    useQuery({
        queryKey: ['objects', selectedObjectId, 'versions'],
        queryFn: async () => {
            if (!selectedObjectId) return []
            const response = await objectsApi.getVersions(selectedObjectId)
            const backendData = response.data as BackendResponse<ObjectVersion[]>
            const versions = (backendData as any)?.data || backendData
            setVersionsForObject(Array.isArray(versions) ? versions : [])
            return versions
        },
        enabled: !!selectedObjectId,
    })

    return (
        <div className="flex items-end gap-2 p-3 border rounded-lg">
            <div className="flex-1">
                <FormField
                    control={form.control}
                    name={`servers.${index}.computeObjectId`}
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel className="text-xs">Object ID</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value || undefined}>
                                <FormControl>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select compute object" />
                                    </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                    {computeObjects.map((obj) => (
                                        <SelectItem key={obj.ObjectID} value={obj.ObjectID}>
                                            {obj.ObjectID} - {obj.ObjectDescription}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <FormMessage />
                        </FormItem>
                    )}
                />
            </div>
            <div className="flex-1">
                <FormField
                    control={form.control}
                    name={`servers.${index}.computeObjectVersionId`}
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel className="text-xs">Version</FormLabel>
                            <Select
                                onValueChange={(val) => field.onChange(parseInt(val, 10))}
                                value={field.value ? String(field.value) : undefined}
                                disabled={!selectedObjectId}
                            >
                                <FormControl>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select version" />
                                    </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                    {versionsForObject.map((v) => (
                                        <SelectItem key={v.versionID} value={String(v.versionID)}>
                                            {v.objectSecondary} - {v.vaultDatetime || 'No date'}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <FormMessage />
                        </FormItem>
                    )}
                />
            </div>
            <Button type="button" variant="ghost" size="sm" onClick={onRemove} className="shrink-0">
                <X className="h-4 w-4" />
            </Button>
        </div>
    )
}
